// IntegrationGate — pathless layout route.
// Redirects to /connect when the tenant has no active Bubble integration.
// Uses a shared React Query cache key so the integrations list is fetched
// only once even if IntegrationsCard also mounts.

import { Navigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@avagency/frontend/lib/api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { fetchIntegrations, type Integration } from "../api";

export function IntegrationGate() {
  const q = useQuery<Integration[]>({
    queryKey: ["bubble", "integrations"],
    queryFn: async () => {
      try {
        return await fetchIntegrations();
      } catch (err) {
        // 503 → integrations adapter not wired (dev/partial deploy).
        // Treat as empty so the gate passes — matches IntegrationsCard behaviour.
        if (err instanceof ApiError && err.status === 503) {
          return [];
        }
        throw err;
      }
    },
  });

  if (q.isLoading) return <LoadingState rows={3} />;
  if (q.error) {
    return <ErrorState error={q.error} retry={() => q.refetch()} />;
  }

  // Require an active Bubble integration — only provider=bubble qualifies.
  const hasActive = (q.data ?? []).some(
    (i) => i.provider === "bubble" && i.status === "active"
  );
  if (!hasActive) return <Navigate to="/connect" replace />;

  return <Outlet />;
}
